import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category?type=movie" },
  { label: "电视剧", to: "/category?type=tv" },
  { label: "热门", to: "/category?sort=hot" },
  { label: "专题", to: "/#topics" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const isActive = (to: string) => {
    const base = to.split("?")[0];
    if (base === "/") return location.pathname === "/";
    return location.pathname.startsWith(base.split("#")[0]) && base !== "/";
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-primary-900"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-clapperboard-fill text-xl"></i>
          </span>
          <span
            className={`font-heading text-xl font-bold tracking-wide ${
              scrolled ? "text-foreground-950" : "text-background-50"
            }`}
          >
            色色影院
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive(link.to)
                  ? scrolled
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-50/15 text-background-50"
                  : scrolled
                    ? "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                    : "text-background-100/90 hover:bg-background-50/10 hover:text-background-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden md:block">
          <div
            className={`flex h-10 items-center gap-2 rounded-full border px-3 transition-colors ${
              scrolled
                ? "border-background-300 bg-background-100"
                : "border-background-50/25 bg-background-50/10"
            }`}
          >
            <i
              className={`ri-search-line text-base ${
                scrolled ? "text-foreground-500" : "text-background-100"
              }`}
            ></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影、电视剧、演员"
              className={`w-44 bg-transparent text-sm outline-none xl:w-56 ${
                scrolled
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-100/70"
              }`}
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`ml-auto flex h-10 w-10 items-center justify-center rounded-md lg:hidden ${
            scrolled ? "text-foreground-900" : "text-background-50"
          }`}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3">
            <div className="flex h-11 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、电视剧、演员"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50"
              >
                搜索
              </button>
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="rounded-md bg-background-100 px-4 py-3 text-sm font-medium text-foreground-800"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}